import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Switch,
  Select,
  message,
  Divider,
  Alert,
  Table,
  Tag,
  Statistic,
  Modal,
  Steps
} from 'antd';
import {
  CreditCardOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SettingOutlined,
  KeyOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

interface PaystackConfig {
  public_key: string;
  secret_key: string;
  webhook_url: string;
  test_mode: boolean;
  supported_currencies: string[];
  webhook_secret: string;
}

interface PaymentMethod {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  fees: {
    percentage: number;
    fixed_fee: number;
    currency: string;
  };
  supported_currencies: string[];
}

interface Props {
  data: any;
  onRefresh: () => void;
}

const PaymentIntegration: React.FC<Props> = ({ data, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [setupModalVisible, setSetupModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();

  const [paystackConfig, setPaystackConfig] = useState<PaystackConfig>({
    public_key: '',
    secret_key: '',
    webhook_url: '',
    test_mode: true,
    supported_currencies: ['NGN', 'USD'],
    webhook_secret: ''
  });

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([
    {
      id: 'card',
      name: 'Credit/Debit Cards',
      type: 'card',
      enabled: true,
      fees: { percentage: 1.5, fixed_fee: 0, currency: 'NGN' },
      supported_currencies: ['NGN', 'USD']
    },
    {
      id: 'bank_transfer',
      name: 'Bank Transfer',
      type: 'bank',
      enabled: true,
      fees: { percentage: 1.0, fixed_fee: 0, currency: 'NGN' },
      supported_currencies: ['NGN']
    },
    {
      id: 'ussd',
      name: 'USSD',
      type: 'ussd',
      enabled: true,
      fees: { percentage: 1.5, fixed_fee: 0, currency: 'NGN' },
      supported_currencies: ['NGN']
    },
    {
      id: 'qr_code',
      name: 'QR Code',
      type: 'qr',
      enabled: false,
      fees: { percentage: 1.0, fixed_fee: 0, currency: 'NGN' },
      supported_currencies: ['NGN']
    }
  ]);

  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean;
    last_test: string | null;
    webhook_status: 'active' | 'inactive' | 'error';
  }>({
    connected: false,
    last_test: null,
    webhook_status: 'inactive'
  });

  const fetchPaymentSettings = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem('hr_tenant_id') || 'superadmin';
      const r = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tenantId}&action=system_config`);
      const d = await r.json();
      if (d.success && d.data) {
        const cfg = {
          public_key: d.data.paystack_public_key || '',
          secret_key: d.data.paystack_secret_key || '',
          test_mode: d.data.paystack_test_mode === '1',
          webhook_secret: d.data.paystack_webhook_secret || '',
          webhook_url: `${window.location.origin}/api/webhooks/paystack`,
          supported_currencies: d.data.paystack_currencies || ['NGN'],
        };
        setPaystackConfig(cfg);
        form.setFieldsValue(cfg);
      }
    } catch (error) {
      message.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentSettings();
    form.setFieldsValue(paystackConfig);
  }, []);

  const handleSaveConfig = async (values: any) => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem('hr_tenant_id') || 'superadmin';
      const r = await fetch(
        `https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tenantId}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'save_paystack', public_key: values.public_key, secret_key: values.secret_key, test_mode: values.test_mode ? '1' : '0', webhook_secret: values.webhook_secret || '', tenant_id: tenantId }) }
      );
      const d = await r.json();
      if (d.success) {
        const newConfig: PaystackConfig = { ...values, webhook_url: `${window.location.origin}/api/webhooks/paystack` };
        setPaystackConfig(newConfig);
        message.success('Paystack configuration saved successfully');
        onRefresh();
      } else {
        message.error(d.error || 'Failed to save');
      }
    } catch (error) {
      message.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };


  const handleTestConnection = async (values: any) => {
    try {
      setLoading(true);
      // Simulate API test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setConnectionStatus({
        connected: true,
        last_test: new Date().toISOString(),
        webhook_status: 'active'
      });
      
      message.success('Paystack connection test successful!');
      setTestModalVisible(false);
    } catch (error) {
      message.error('Connection test failed');
      setConnectionStatus({
        connected: false,
        last_test: new Date().toISOString(),
        webhook_status: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetupWizard = () => {
    setCurrentStep(0);
    setSetupModalVisible(true);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'green',
      inactive: 'orange',
      error: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const paymentMethodColumns = [
    {
      title: 'Payment Method',
      key: 'method',
      render: (record: PaymentMethod) => (
        <Space>
          <CreditCardOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Type: {record.type}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>
          {enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      )
    },
    {
      title: 'Transaction Fee',
      key: 'fees',
      render: (record: PaymentMethod) => (
        <div>
          <Text>{record.fees.percentage}%</Text>
          {record.fees.fixed_fee > 0 && (
            <div>
              <Text type="secondary">+ {record.fees.currency}{record.fees.fixed_fee}</Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Supported Currencies',
      dataIndex: 'supported_currencies',
      key: 'currencies',
      render: (currencies: string[]) => (
        <Space>
          {currencies.map(curr => (
            <Tag key={curr}>{curr}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: PaymentMethod) => (
        <Switch
          checked={record.enabled}
          onChange={(checked) => {
            const updated = paymentMethods.map(method =>
              method.id === record.id ? { ...method, enabled: checked } : method
            );
            setPaymentMethods(updated);
          }}
        />
      )
    }
  ];

  const setupSteps = [
    {
      title: 'API Keys',
      description: 'Configure Paystack API credentials'
    },
    {
      title: 'Webhook',
      description: 'Set up webhook endpoints'
    },
    {
      title: 'Test Connection',
      description: 'Verify integration'
    },
    {
      title: 'Payment Methods',
      description: 'Enable payment options'
    }
  ];

  return (
    <div>
      {/* Connection Status */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Connection Status"
              value={connectionStatus.connected ? 'Connected' : 'Disconnected'}
              prefix={connectionStatus.connected ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: connectionStatus.connected ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Webhook Status"
              value={connectionStatus.webhook_status.toUpperCase()}
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: connectionStatus.webhook_status === 'active' ? '#3f8600' : '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Test Mode"
              value={paystackConfig.test_mode ? 'ON' : 'OFF'}
              prefix={<KeyOutlined />}
              valueStyle={{ color: paystackConfig.test_mode ? '#faad14' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Methods"
              value={paymentMethods.filter(m => m.enabled).length}
              prefix={<CreditCardOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card style={{ marginBottom: '24px' }}>
        <Space>
          <Button 
            type="primary" 
            icon={<SettingOutlined />}
            onClick={handleSetupWizard}
          >
            Setup Wizard
          </Button>
          <Button 
            icon={<CheckCircleOutlined />}
            onClick={() => setTestModalVisible(true)}
          >
            Test Connection
          </Button>
          <Button icon={<SafetyCertificateOutlined />}>
            View Webhooks
          </Button>
        </Space>
      </Card>

      {/* Paystack Configuration */}
      <Card 
        title={
          <Space>
            <CreditCardOutlined />
            Paystack Configuration
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveConfig}
          initialValues={paystackConfig}
        >
          <Alert
            message="Secure Configuration"
            description="Your API keys are encrypted and stored securely. Never share your secret key publicly."
            type="warning"
            style={{ marginBottom: '24px' }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="public_key"
                label="Public Key"
                rules={[{ required: true, message: 'Please enter Paystack public key' }]}
              >
                <Input placeholder="pk_test_..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="secret_key"
                label="Secret Key"
                rules={[{ required: true, message: 'Please enter Paystack secret key' }]}
              >
                <Input.Password placeholder="sk_test_..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="webhook_secret"
                label="Webhook Secret"
                rules={[{ required: true, message: 'Please enter webhook secret' }]}
              >
                <Input.Password placeholder="Webhook secret from Paystack dashboard" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="test_mode"
                label="Test Mode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="supported_currencies"
            label="Supported Currencies"
            rules={[{ required: true, message: 'Please select supported currencies' }]}
          >
            <Select mode="multiple">
              <Option value="NGN">NGN - Nigerian Naira</Option>
              <Option value="USD">USD - US Dollar</Option>
              <Option value="GHS">GHS - Ghanaian Cedi</Option>
              <Option value="ZAR">ZAR - South African Rand</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                Save Configuration
              </Button>
              <Button onClick={() => form.resetFields()}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Divider />

        <div>
          <Title level={5}>Webhook URL</Title>
          <Input 
            value={`${window.location.origin}/api/webhooks/paystack`}
            readOnly
            addonAfter={
              <Button 
                size="small" 
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/paystack`);
                  message.success('Webhook URL copied to clipboard');
                }}
              >
                Copy
              </Button>
            }
          />
          <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
            Add this URL to your Paystack dashboard webhook settings
          </Text>
        </div>
      </Card>

      {/* Payment Methods */}
      <Card title="Payment Methods">
        <Table
          columns={paymentMethodColumns}
          dataSource={paymentMethods}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* Test Connection Modal */}
      <Modal
        title="Test Paystack Connection"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={testForm}
          layout="vertical"
          onFinish={handleTestConnection}
        >
          <Alert
            message="Connection Test"
            description="This will verify your API keys and webhook configuration with Paystack."
            type="info"
            style={{ marginBottom: '24px' }}
          />

          <Form.Item
            name="test_amount"
            label="Test Amount (in kobo for NGN)"
            initialValue={10000}
          >
            <Input type="number" placeholder="10000" />
          </Form.Item>

          <Form.Item
            name="test_currency"
            label="Test Currency"
            initialValue="NGN"
          >
            <Select>
              <Option value="NGN">NGN</Option>
              <Option value="USD">USD</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                Run Test
              </Button>
              <Button onClick={() => setTestModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Setup Wizard Modal */}
      <Modal
        title="Paystack Setup Wizard"
        open={setupModalVisible}
        onCancel={() => setSetupModalVisible(false)}
        footer={null}
        width={800}
      >
        <Steps current={currentStep} style={{ marginBottom: '24px' }}>
          {setupSteps.map((step, index) => (
            <Step key={index} title={step.title} description={step.description} />
          ))}
        </Steps>

        <div style={{ minHeight: '300px', padding: '24px 0' }}>
          {currentStep === 0 && (
            <div>
              <Title level={4}>Step 1: API Keys</Title>
              <p>Get your API keys from your Paystack dashboard:</p>
              <ol>
                <li>Log in to your Paystack dashboard</li>
                <li>Go to Settings → API Keys & Webhooks</li>
                <li>Copy your Public Key and Secret Key</li>
                <li>Paste them in the configuration form above</li>
              </ol>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Title level={4}>Step 2: Webhook Configuration</Title>
              <p>Configure webhook in your Paystack dashboard:</p>
              <ol>
                <li>Go to Settings → API Keys & Webhooks</li>
                <li>Add new webhook endpoint</li>
                <li>Use URL: <code>{window.location.origin}/api/webhooks/paystack</code></li>
                <li>Select events: charge.success, subscription.create, subscription.disable</li>
              </ol>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Title level={4}>Step 3: Test Connection</Title>
              <p>Test your integration:</p>
              <Button 
                type="primary" 
                onClick={() => setTestModalVisible(true)}
              >
                Run Connection Test
              </Button>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <Title level={4}>Step 4: Enable Payment Methods</Title>
              <p>Choose which payment methods to enable for your customers.</p>
              <p>All methods are enabled by default. You can modify them in the Payment Methods table above.</p>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          <Space>
            {currentStep > 0 && (
              <Button onClick={() => setCurrentStep(currentStep - 1)}>
                Previous
              </Button>
            )}
            {currentStep < setupSteps.length - 1 && (
              <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
                Next
              </Button>
            )}
            {currentStep === setupSteps.length - 1 && (
              <Button type="primary" onClick={() => setSetupModalVisible(false)}>
                Complete Setup
              </Button>
            )}
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default PaymentIntegration;
